"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { CalendarDays } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StandaloneAddressAutocomplete } from "@/components/inputs/address-autocomplete";
import type { ParsedAddress } from "@/components/inputs/address-autocomplete";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { useMutationWithState } from "@/hooks/use-mutation-with-state";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { track } from "@/lib/analytics/tracker";
import { cn } from "@/lib/utils";
import { DeliveryWindowPicker } from "@/components/cart/delivery-window-picker";
import {
  earliestSelectablePickupDate,
  formatDeliveryWindow,
  isValidDeliveryWindow,
  type DeliveryWindow,
} from "@/lib/checkout/delivery-window";
import { RecycleOfferTypeSelector } from "./recycle-offer-type-selector";
import { useMarketplaceFailureReporter } from "./use-marketplace-failure-reporter";
import { RecycleTruckTypeField } from "./recycle-truck-type-field";
import type { EnabledTruck } from "./recycle-truck-capacities";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { useListingQuery } from "./listing-query";

type FulfillmentMode = "pickup" | "delivery";
type OfferType = "pay" | "free" | "charge";

interface RecycleOfferFormProps {
  productId: Id<"products">;
  minOrderQuantity: number;
  maxQuantity?: number;
  pickupAvailable: boolean;
  enabledTrucks: EnabledTruck[];
  targetRebateCents?: number;
  pickupOnly: boolean;
}

const PICKUP_UNAVAILABLE_NOTICE =
  "The seller hasn't added a pickup address yet, so only delivery is available for now.";

function FulfillmentSelector({
  fulfillment,
  pickupAvailable,
  onFulfillmentChange,
}: {
  fulfillment: FulfillmentMode;
  pickupAvailable: boolean;
  onFulfillmentChange: (next: FulfillmentMode) => void;
}) {
  return (
    <>
      <SegmentedControl
        size="compact"
        fullWidth
        aria-label="Fulfillment"
        value={fulfillment}
        onValueChange={onFulfillmentChange}
        options={[
          {
            value: "pickup",
            label: "Pickup",
            disabled: !pickupAvailable,
            title: pickupAvailable ? undefined : PICKUP_UNAVAILABLE_NOTICE,
          },
          { value: "delivery", label: "Delivery" },
        ]}
      />
      {!pickupAvailable ? (
        <p className="text-xs text-muted-foreground">
          {PICKUP_UNAVAILABLE_NOTICE}
        </p>
      ) : null}
    </>
  );
}

function clampBidQuantity(
  value: number,
  minOrderQuantity: number,
  maxQuantity: number | undefined,
): number {
  if (Number.isNaN(value)) return minOrderQuantity;
  return maxQuantity !== undefined
    ? Math.min(maxQuantity, Math.max(minOrderQuantity, value))
    : Math.max(minOrderQuantity, value);
}

function offerSubmitBlocker(form: {
  quantity: number;
  minOrderQuantity: number;
  maxQuantity: number | undefined;
  preferredWindow: DeliveryWindow | null;
  fulfillment: FulfillmentMode;
  hasDeliveryZip: boolean;
  offerType: OfferType;
  offerAmount: string;
}): string | null {
  if (!isValidDeliveryWindow(form.preferredWindow)) {
    return `Choose a ${form.fulfillment} window to continue.`;
  }
  if (form.fulfillment === "delivery" && !form.hasDeliveryZip) {
    return "Enter a delivery address to continue.";
  }
  if (form.offerType !== "free" && !(parseFloat(form.offerAmount) > 0)) {
    return "Enter an offer amount to continue.";
  }
  const withinRange =
    form.quantity >= form.minOrderQuantity &&
    (form.maxQuantity === undefined || form.quantity <= form.maxQuantity);
  return withinRange ? null : "Complete all fields to submit.";
}

function trackClaim(
  productId: Id<"products">,
  offerType: OfferType,
  fulfillment: FulfillmentMode,
  quantity: number,
  result: { leadId: string; quoteId?: string },
) {
  if (offerType !== "free") return;
  const { leadId, quoteId } = result;
  track("claim.submitted", {
    productId,
    offerType,
    fulfillment,
    quantity,
    leadId,
    quoteId,
  });
}

export function RecycleOfferForm({
  productId,
  minOrderQuantity,
  maxQuantity,
  pickupAvailable,
  enabledTrucks,
  targetRebateCents,
  pickupOnly,
}: RecycleOfferFormProps) {
  const searchParams = useListingQuery();

  const [fulfillment, setFulfillment] = useState<FulfillmentMode>(
    pickupOnly || pickupAvailable ? "pickup" : "delivery",
  );
  const [quantity, setQuantity] = useState(() =>
    clampBidQuantity(
      parseInt(searchParams.get("qty") ?? "", 10),
      minOrderQuantity,
      maxQuantity,
    ),
  );
  const [truckType, setTruckType] = useState(enabledTrucks[0]?.slug);
  const [preferredWindow, setPreferredWindow] = useState<DeliveryWindow | null>(
    null,
  );
  const windowLabel = formatDeliveryWindow(preferredWindow);
  const [offerType, setOfferType] = useState<OfferType>("free");
  const [offerAmount, setOfferAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [parsedAddress, setParsedAddress] = useState<ParsedAddress | null>(
    null,
  );
  const [addressDisplay, setAddressDisplay] = useState("");
  const router = useRouter();

  const reportMarketplaceFailure = useMarketplaceFailureReporter({
    productId,
    fulfillment,
    offerType,
    quantity,
  });

  const { mutate, error, clearError, isLoading } = useMutationWithState(
    api.quotes.mutations.vendorQuote.submitBuyerBid,
    { onError: reportMarketplaceFailure },
  );

  const handleAddressChange = useCallback(
    (display: string, parsed: ParsedAddress | null) => {
      setAddressDisplay(display);
      setParsedAddress(parsed);
    },
    [],
  );

  const handleQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      setQuantity(clampBidQuantity(val, minOrderQuantity, maxQuantity));
    },
    [minOrderQuantity, maxQuantity],
  );

  const submitBlocker = offerSubmitBlocker({
    quantity,
    minOrderQuantity,
    maxQuantity,
    preferredWindow,
    fulfillment,
    hasDeliveryZip: Boolean(parsedAddress?.zip),
    offerType,
    offerAmount,
  });
  const canSubmit = submitBlocker === null;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || isLoading) return;

    const amountCents =
      offerType !== "free"
        ? Math.round(parseFloat(offerAmount) * 100)
        : undefined;

    const bidResult = await mutate({
      sourceProductId: productId,
      quantity,
      fulfillmentPreference: fulfillment,
      truckType: fulfillment === "pickup" ? truckType : undefined,
      preferredDate: preferredWindow?.start ?? "",
      preferredDateEnd: preferredWindow?.end,
      offerType,
      offerAmountCents: amountCents,
      notes: notes.trim() || undefined,
      deliveryAddress:
        fulfillment === "delivery" && parsedAddress
          ? {
              line1: parsedAddress.line1,
              city: parsedAddress.city,
              state: parsedAddress.state,
              postalCode: parsedAddress.zip,
            }
          : undefined,
    });

    if (!bidResult) return;
    trackClaim(productId, offerType, fulfillment, quantity, bidResult);

    router.push(`/dashboard/orders/${bidResult.quoteId ?? bidResult.leadId}`);
  }, [
    canSubmit,
    isLoading,
    mutate,
    productId,
    quantity,
    fulfillment,
    truckType,
    preferredWindow,
    offerType,
    offerAmount,
    notes,
    parsedAddress,
    router,
  ]);

  return (
    <div className="space-y-4">
      {pickupOnly ? null : (
        <FulfillmentSelector
          fulfillment={fulfillment}
          pickupAvailable={pickupAvailable}
          onFulfillmentChange={(next) => {
            setFulfillment(next);
            if (next === "delivery" && offerType === "charge")
              setOfferType("free");
          }}
        />
      )}

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">Quantity</span>
        <Input
          type="number"
          min={minOrderQuantity}
          max={maxQuantity}
          value={quantity}
          onChange={handleQuantityChange}
        />
      </label>

      {fulfillment === "pickup" ? (
        <RecycleTruckTypeField
          enabledTrucks={enabledTrucks}
          value={truckType}
          onChange={setTruckType}
        />
      ) : null}

      {fulfillment === "delivery" ? (
        <label className="block space-y-1">
          <span className="text-xs font-medium text-foreground">
            Delivery address
          </span>
          <StandaloneAddressAutocomplete
            value={addressDisplay}
            onChange={handleAddressChange}
            placeholder="Enter delivery ZIP or address"
          />
        </label>
      ) : null}

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">
          {fulfillment === "pickup" ? "Pickup window" : "Delivery window"}
        </span>
        <DeliveryWindowPicker
          window={preferredWindow}
          onWindowChange={setPreferredWindow}
          earliestDate={earliestSelectablePickupDate()}
        >
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full justify-between",
              !windowLabel && "text-muted-foreground",
            )}
          >
            {windowLabel || "Choose dates"}
            <CalendarDays className="h-4 w-4" />
          </Button>
        </DeliveryWindowPicker>
      </label>

      {pickupOnly ? null : (
        <RecycleOfferTypeSelector
          fulfillment={fulfillment}
          offerType={offerType}
          offerAmount={offerAmount}
          targetRebateCents={targetRebateCents}
          onOfferTypeChange={setOfferType}
          onOfferAmountChange={setOfferAmount}
        />
      )}

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">
          Notes{" "}
          <span className="font-normal text-muted-foreground">(optional)</span>
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Special instructions, scheduling preferences..."
          className="flex w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </label>

      <ActionErrorAlert error={error} onDismiss={clearError} />

      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={!canSubmit || isLoading}
        className="w-full"
      >
        {isLoading
          ? "Submitting..."
          : offerType === "free"
            ? "Claim now"
            : "Submit Offer"}
      </Button>

      {!canSubmit ? (
        <p className="text-center text-xs text-muted-foreground">
          {submitBlocker}
        </p>
      ) : null}
    </div>
  );
}
